import { fetcher } from '@/services/graphql/fetcher/index';
import { PlaceOrderType, type AnyObj } from '@/types';
import { businessUtils } from '@/utils';
import {
  fetchActiveOrderByTypeDocument,
  fetchAddItemToOrderByOrderTypeDocument,
  fetchAdjustOrderLineDocument,
  fetchApplyShoppingCreditsDocument,
  fetchCancelShoppingCreditsDocument,
  fetchCheckProductCanCheckoutDocument,
  fetchCreateWxChatPaymentIntentDocument,
  fetchEligibleShippingMethodsByOrderTypeDocument,
  fetchGetOrderIsIncludeMemberPriceDocument,
  fetchGetPurchaseQuantityDocument,
  fetchGetShoppingCartDocument,
  fetchNewSetOrderCustomFieldsDocument,
  fetchOrderPromotionResultDocument,
  fetchRemoveAllOrderLinesByOrderTypeDocument,
  fetchRemoveOrderLineDocument,
  fetchSetOrderCustomFieldsDocument,
  fetchSetShippingAddressDocument,
  fetchShoppingCartSettlementDocument,
  fetchUpdateOrderShippingAddressDocument,
  fetchUpdateShoppingCartSkuDocument,
  setShippingMethodByOrderTypeDocument,
  fetchActivityCountdownByCartDocument,
} from '../graphql/docs';

/**
 * 获取购物车和预下单信息
 * @param isRemoveMarkUp
 * @returns
 */
export const fetchGetShoppingCart = async (
  isRemoveMarkUp: boolean = false,
  isUseMember: boolean = true,
  isUseShoppingCredit: boolean = true
) => {
  const res = await fetcher.query(fetchGetShoppingCartDocument, {
    variables: {
      isRemoveMarkUp,
      isUseMember,
      isUseShoppingCredit,
    },
  });
  return res.data.value?.getShoppingCart;
};

/**
 * 获取购物车的活跃订单信息
 */
export const fetchActiveOrderByShoppingTrolley = async (isRemoveMarkUp: boolean = false) => {
  const res = await fetcher.query(fetchActiveOrderByTypeDocument, {
    variables: {
      type: PlaceOrderType.shoppingTrolley,
      isRemoveMarkUp,
    },
  });
  return res.data.value?.getActiveOrderByType;
};

/**
 * 加入购物车
 * @param variables - 请求参数{productVariantId, quantity}
 * @returns
 */
export const fetchAddItemToOrderByShoppingTrolley = async (variables: AnyObj) => {
  variables = { ...variables, type: PlaceOrderType.shoppingTrolley };
  const res = await fetcher.mutation(fetchAddItemToOrderByOrderTypeDocument, variables);
  return res.data?.addItemToOrderByOrderType;
};

/**
 * 加入购物车 - 预下单
 * @param variables - 请求参数{productVariantId, quantity}
 * @returns
 */
export const fetchAddItemToOrderByOutrightPurchase = async (variables: AnyObj) => {
  variables = { ...variables, type: PlaceOrderType.outrightPurchase };
  const res = await fetcher.mutation(fetchAddItemToOrderByOrderTypeDocument, variables);
  return res.data?.addItemToOrderByOrderType;
};

/**
 * 添加至订单 - 直接购买
 * @param variables - {productVariantId, quantity}
 */
export const fetchAddItemToOrderByRegularOrder = async (variables: AnyObj) => {
  variables = { ...variables, type: PlaceOrderType.regularOrder };
  const res = await fetcher.mutation(fetchAddItemToOrderByOrderTypeDocument, variables);
  return res.data?.addItemToOrderByOrderType;
};

/**
 * 积分商品兑换
 * @param variables
 * @returns
 */
export const fetchAddItemToOrderByPointsExchange = async (variables: AnyObj) => {
  variables = { ...variables, type: PlaceOrderType.regularOrder, buyType: 'pointsExchange' };
  const res = await fetcher.mutation(fetchAddItemToOrderByOrderTypeDocument, variables);
  return res.data?.addItemToOrderByOrderType;
};

/**
 * 根据id删除购物车 - 单选
 * @param id - 订单购物车id
 */
export const fetchRemoveOrderLineByShoppingTrolley = async (orderLineIds: string[]) => {
  const res = await fetcher.mutation(fetchRemoveOrderLineDocument, {
    orderLineIds,
    type: PlaceOrderType.shoppingTrolley,
  });
  return res.data?.removeOrderLineByOrderType;
};

/**
 * 根据id修改购物车项的数量
 * @param orderLineId - 订单购物车id
 * @param count - 修改的数量
 */
export const fetchModifyCartItemCountByShoppingTrolley = async (
  orderLineId: string,
  count: number
) => {
  const res = await fetcher.mutation(fetchAdjustOrderLineDocument, {
    orderLineId,
    quantity: count,
    type: PlaceOrderType.shoppingTrolley,
  });
  return res.data;
};

/**
 * 删除全部购物车订单
 */
export const fetchRemoveAllOrderByShoppingTrolley = async () => {
  const res = await fetcher.mutation(fetchRemoveAllOrderLinesByOrderTypeDocument, {
    type: PlaceOrderType.shoppingTrolley,
  });
  return res.data?.removeAllOrderLinesByOrderType;
};

/**
 * 购物车结算 - 预下单
 */
export const fetchSettlementShoppingCart = async (input: AnyObj) => {
  const res = await fetcher.mutation(fetchShoppingCartSettlementDocument, {
    input,
  });
  return res.data?.shoppingCartSettlement;
};

/**
 * 修改购物车规格、数量
 * @param variables - 请求参数
 * @returns
 */
export const fetchUpdateShoppingCartSku = async (variables: AnyObj) => {
  const res = await fetcher.mutation(fetchUpdateShoppingCartSkuDocument, variables);
  return res.data?.updateShoppingCartSku;
};

/**
 * 获取购物车优惠活动信息
 * @param orderIds - [购物车id, 预下单id]
 */
export const fetchOrderPromotionResult = async (orderIds: string[]) => {
  const res = await fetcher.query(fetchOrderPromotionResultDocument, {
    variables: {
      orderIds,
    },
  });
  return res.data.value?.orderPromotionResult;
};

/**
 * 查询商品限购数量
 * @param productId - 商品id
 */
export const fetchGetPurchaseQuantity = async (
  productId: string,
  type: PlaceOrderType = PlaceOrderType.regularOrder,
  isIncludeCurrentOrder: boolean = true
) => {
  const res = await fetcher.query(fetchGetPurchaseQuantityDocument, {
    variables: {
      productId,
      type,
      isIncludeCurrentOrder,
    },
  });
  return res.data.value?.getPurchaseQuantity;
};

/**
 * 设置发货地址
 * @param input - 地址输入数据
 */
export const fetchSetShippingAddress = async (input: AnyObj, orderId: string) => {
  const variables = { input, orderId };
  const res = await fetcher.mutation(fetchSetShippingAddressDocument, variables);
  return res.data?.setShippingAddress;
};

/**
 * 更新发货地址
 * @param variables - {input, orderId}
 */
export const fetchUpdateOrderShippingAddress = async (variables: AnyObj) => {
  const res = await fetcher.mutation(fetchUpdateOrderShippingAddressDocument, variables);
  return res.data?.updateOrderShippingAddress;
};

/**
 * 获取直接购买的活跃订单信息
 */
export const fetchActiveOrderByRegularOrder = async () => {
  const res = await fetcher.query(fetchActiveOrderByTypeDocument, {
    variables: {
      type: PlaceOrderType.regularOrder,
    },
  });
  return res.data.value?.getActiveOrderByType;
};

/**
 * 删除全部订单 - 直接购买
 */
export const fetchRemoveAllOrderByRegularOrder = async () => {
  const res = await fetcher.mutation(fetchRemoveAllOrderLinesByOrderTypeDocument, {
    type: PlaceOrderType.regularOrder,
  });
  return res.data?.removeAllOrderLinesByOrderType;
};

/**
 * 买家留言
 */
export const fetchNewSetOrderCustomFields = async (variables: AnyObj) => {
  const res = await fetcher.mutation(fetchNewSetOrderCustomFieldsDocument, variables);
  return res.data?.newSetOrderCustomFields;
};

/**
 * 微信下单
 */
export const fetchCreateWxChatPaymentIntent = async (variables: AnyObj) => {
  const res = await fetcher.mutation(
    fetchCreateWxChatPaymentIntentDocument,
    businessUtils.placeOrder.getWechatPaymentVariables(variables)
  );
  return res.data?.createWxChatPaymentIntent;
};

/**
 * 获取发货方式 - 直接下单
 */
export const fetchEligibleShippingMethodsByRegularOrder = async () => {
  const res = await fetcher.query(fetchEligibleShippingMethodsByOrderTypeDocument, {
    variables: {
      type: PlaceOrderType.regularOrder,
    },
  });
  return res.data.value?.eligibleShippingMethodsByOrderType;
};

/**
 * 获取发货方式 - 预下单
 */
export const fetchEligibleShippingMethodsByOutrightPurchase = async () => {
  const res = await fetcher.query(fetchEligibleShippingMethodsByOrderTypeDocument, {
    variables: {
      type: PlaceOrderType.outrightPurchase,
    },
  });
  return res.data.value?.eligibleShippingMethodsByOrderType;
};

/**
 * 设置发货方式 - 直接下单
 */
export const fetchSetShippingMethodByRegularOrder = async (shippingMethodId: string) => {
  const variables = {
    type: PlaceOrderType.regularOrder,
    shippingMethodId,
  };
  const res = await fetcher.mutation(setShippingMethodByOrderTypeDocument, variables);
  return res.data.setShippingMethodByOrderType;
};

/**
 * 设置发货方式 - 预下单
 */
export const fetchSetShippingMethodByOutrightPurchase = async (shippingMethodId: string) => {
  const variables = {
    type: PlaceOrderType.outrightPurchase,
    shippingMethodId,
  };
  const res = await fetcher.mutation(setShippingMethodByOrderTypeDocument, variables);
  return res.data.setShippingMethodByOrderType;
};

/**
 * 设置下单自定义字段
 */
export const fetchSetOrderCustomFields = async (variables: AnyObj) => {
  const res = await fetcher.mutation(fetchSetOrderCustomFieldsDocument, variables);
  return res.data.setOrderCustomFields;
};

/**
 * 查询当前订单中的商品是否参与了会员价活动
 * @param orderId - 订单id
 * @returns
 */
export const fetchGetOrderIsIncludeMemberPrice = async (orderId: string) => {
  const res = await fetcher.query(fetchGetOrderIsIncludeMemberPriceDocument, {
    variables: { orderId },
  });
  return res.data.value?.getOrderIsIncludeMemberPrice;
};

/**
 * 查询结算订单中的商品是否都是限购
 * @param skuIds - 结算订单的商品规格id数组
 * @returns
 */
export const fetchCheckProductCanCheckout = async (skuIds: string[]) => {
  const res = await fetcher.query(fetchCheckProductCanCheckoutDocument, {
    variables: { skuIds },
  });
  return res.data.value?.checkProductCanCheckout;
};

/**
 * 申请购物金抵扣
 */
export const fetchApplyShoppingCredits = async (orderId: string) => {
  const res = await fetcher.mutation(fetchApplyShoppingCreditsDocument, { orderId });
  return res.data?.applyShoppingCredits;
};

/**
 * 取消购物金抵扣
 */
export const fetchCancelShoppingCredits = async (orderId: string) => {
  const res = await fetcher.mutation(fetchCancelShoppingCreditsDocument, { orderId });
  return res.data?.cancelShoppingCredits;
};

/**
 * 查询购物车页面是否有活动倒计时
 * @returns
 */
export const fetchActivityCountdownByCart = async () => {
  const res = await fetcher.query(fetchActivityCountdownByCartDocument, {});
  return res.data.value?.activityCountdownByCart;
};
